import type { AIVoice } from 'js/src/client';

// "Standard"-type accents ("Standard", "Pl Standard", "Modern Standard", …)
// mean "no distinctive accent": multilingual voices carry one per supported
// language, so they flood the trait line without telling the agent anything.
// Only distinctive accents (Canadian, Us Midwest, Yorkshire, …) are shown.
const NON_DISTINCTIVE_ACCENT = /\bstandard\b/i;

export interface FormatVoiceTraitsOptions {
    /**
     * Cap the language list at this many entries and render the overflow as a
     * count ("21 languages incl. English, Spanish, Arabic"). Omit to list every
     * language.
     */
    maxLanguages?: number;
}

/**
 * One compact " · "-joined trait line for an AI voice, for LLM consumption:
 * gender, age, tone, style, then labeled distinctive accents and languages.
 * Quality is omitted — voice search already gates on it, so it carries no
 * signal. Returns '' for voices with no usable attributes (common for clones).
 */
export function formatVoiceTraits(voice: AIVoice, options?: FormatVoiceTraitsOptions): string {
    const attrs = voice.attributes;
    const parts: string[] = [...attrs.gender, ...attrs.age, ...attrs.tone, ...attrs.style];

    const accents = attrs.accent.filter((accent) => !NON_DISTINCTIVE_ACCENT.test(accent));
    if (accents.length > 0) {
        parts.push(`accent: ${accents.join(', ')}`);
    }

    const languages = attrs.language;
    if (languages.length > 0) {
        const max = options?.maxLanguages;
        if (max !== undefined && languages.length > max) {
            parts.push(`${languages.length} languages incl. ${languages.slice(0, max).join(', ')}`);
        } else {
            parts.push(`languages: ${languages.join(', ')}`);
        }
    }

    return parts.join(' · ');
}
